import { ApiAccountRole, ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { fetchUsersWithConnections } from 'src/Libs/user/api/user.api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { createAccountLink } from './account-links.api'

export interface GrantInstallerAccessData {
  installationId: number
  userId: number
  writePermission: boolean
  reason: string
}

/**
 * Grants a Flow installer access to an installation.
 *
 * Flow installers are individual Hager users. The installer is selected by its numeric Portal user
 * ID, but the account-link endpoint expects the Hager SSO UUID (`user.ssoId`) as the `accountId`,
 * so the SSO UUID is resolved first and then used to create the `user`/`installer` account link.
 */
export const grantInstallerAccess = async (
  { installationId, userId, writePermission, reason }: GrantInstallerAccessData,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  try {
    if (!Number.isSafeInteger(installationId) || installationId <= 0 || !Number.isSafeInteger(userId) || userId <= 0) {
      throw new Error('Invalid installation or installer ID')
    }

    const users = await fetchUsersWithConnections(
      {
        connect: 'location',
        fields: 'user.ID,user.ssoId',
        filter: `user.ID=${userId},user.source=hager,user.level=INSTALLER`,
      },
      authFetch
    )

    if (users.hasErrors) {
      throw users.error ?? new Error('Could not resolve the Hager installer')
    }

    const accountId = users.value?.length === 1 ? users.value[0]['user.ssoId'] : undefined

    if (!accountId) {
      throw new Error('The Hager installer has no unique SSO account')
    }

    const permissionType = writePermission ? 'rw' : 'ro'

    return await createAccountLink({
      installationId,
      authFetch,
      data: {
        accountId,
        accountSource: 'hager',
        accountType: 'user',
        role: 'installer' as ApiAccountRole,
        permissionType,
        storagePermissionType: permissionType,
        reason,
      },
    })
  } catch (error) {
    return handleFetchError<void>(error)
  }
}
